import { useQuery } from '@tanstack/react-query';
import { useEnabledQuery } from '../../../ui/hooks/useEnabledQuery';
import { ResourceType } from '../../enumerations/ResourceType';
import { ParentedArtifactResource } from '../artifactResourceTypes';
import { AddOrSetMutateObject, AddOrSetMutateObjectSchema } from '../mutateObjects';
import { PersistableSchemaType } from '../persistableResourceTypes';
import { useMutationWithDelay } from '../useMutationWithDelay';
import { useResourceAdapterActions } from './useResourceAdapterActions';

export function useSingleResourceSingleParentedResourceAdapterActions<R extends ParentedArtifactResource>(
    apiPostfix: string,
    resourceType: ResourceType,
    resourceSchema: PersistableSchemaType<R>
) {
    const actions = useResourceAdapterActions(apiPostfix, resourceType, resourceSchema);
    const getEnabled = useEnabledQuery();

    const useGetSingleResource = (parentId: string | undefined | null) => {
        return useQuery({
            queryKey: [parentId, resourceType],
            queryFn: () => actions.getSingleFromParent(parentId as string),
            enabled: getEnabled(parentId),
        });
    };

    const useSetResource = () => {
        return useMutationWithDelay(
            ({ resource, parentId, withoutGrowl }: AddOrSetMutateObject<R>) => actions.addOrSetResource(resource, parentId as string, undefined, withoutGrowl),
            AddOrSetMutateObjectSchema
        );
    };

    return {
        useValidateResource: actions.useValidateResource,
        useValidateAllResourcesSingleParented: actions.useValidateAllResourcesSingleParented,
        useGetResource: actions.useGetResource,
        useUpdateResource: actions.useUpdateResource,
        useDeleteResource: actions.useDeleteResource,
        processMutationResponse: actions.processMutationResponse,
        processGetResponse: actions.processGetResponse,
        processGetCollectionResponse: actions.processGetCollectionResponse,
        processGetResourceResponse: actions.processGetResourceResponse,
        useGetSingleResource,
        useSetResource,
    };
}
